import type {QueryKey, UseQueryResult} from '@tanstack/react-query'
import {hashQueryKey, useQueryClient} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'

import {DefaultSearchConfig} from '../../../components/filter-bar/search-context'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import {useViews} from '../../../hooks/use-views'
import {
  buildMemexItemsPageQueryKey,
  getGroupIdFromQueryKey,
  type GroupId,
  isQueryKeyForGroups,
  type PaginatedMemexItemsQueryKey,
} from './query-keys'
import {
  type MemexItemGroupsPageQueryData,
  type MemexItemsPageQueryData,
  type PageParam,
  pageParamForInitialPage,
  type PaginatedMemexItemsQueryVariables,
} from './types'
import {useMemexItemsActiveQueriesMetadata} from './use-memex-items-active-queries-metadata'
import {usePaginatedMemexItemsActiveQueries} from './use-paginated-memex-items-active-queries'

export function usePaginatedMemexItemsQuery() {
  const variables = usePaginatedMemexItemsQueryVariables()
  const {queries, queryKeys} = usePaginatedMemexItemsActiveQueries(variables)

  // Our queries from usePaginatedMemexItemsActiveQueries can be 1 of 2 types:
  // 1. A query for a page of memex items (queriesForItems: Array<MemexItemsPageQueryData>)
  // 2. A query for a page of groups (queriesForGroups: Array<MemexItemGroupsPageQueryData>)
  // We need to separate these into 2 arrays, so that consumers of this hook can easily
  // stitch together items data based on those pages, but also provide access to the
  // groups pages for looking up information about the groups and their `pageInfo`.
  const {queriesForItems, queryKeysForItems, queriesForGroups, queryKeysForGroups} = useMemo(() => {
    return separateQueriesForItemsAndGroups(queries, queryKeys)
  }, [queries, queryKeys])

  const data = queriesForItems.map(
    pageQuery => pageQuery.data || {nodes: [], pageInfo: {hasNextPage: false, hasPreviousPage: false}},
  )

  const itemQueriesByGroup = useMemo(() => {
    return groupItemQueriesByGroupId(queriesForItems, queryKeysForItems, queriesForGroups)
  }, [queriesForGroups, queriesForItems, queryKeysForItems])

  const result = {
    data,
    queriesForItems,
    queryKeysForItems,
    queriesForGroups,
    queryKeysForGroups,
    itemQueriesByGroup,
    ...useMemexItemsPagination(queriesForItems, queriesForGroups, queryKeysForItems, variables),
    ...useMemexItemsGroupPagination(queriesForItems, queryKeysForItems, variables),
  }
  return result
}

function usePaginatedMemexItemsQueryVariables() {
  const {currentView} = useViews()
  const q = DefaultSearchConfig.getSearchQueryFromView(currentView)
  const {sorts} = useSortedBy()
  const sortedBy = useMemo(
    () => sorts.map(sort => ({columnId: sort.column.databaseId, direction: sort.direction})),
    [sorts],
  )
  const {groupedByColumnId: horizontalGroupedByColumnId} = useHorizontalGroupedBy()
  return useMemo(() => ({q, sortedBy, horizontalGroupedByColumnId}), [q, sortedBy, horizontalGroupedByColumnId])
}

function useMemexItemsPagination(
  pageQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  groupQueries: Array<UseQueryResult<MemexItemGroupsPageQueryData>>,
  queryKeys: Array<QueryKey>,
  variables: PaginatedMemexItemsQueryVariables,
) {
  const {getActiveQueriesMetadata, setActiveQueriesMetadata} = useMemexItemsActiveQueriesMetadata(variables)
  const fetchNextPage = useCallback(() => {
    const currentActiveQueryData = getActiveQueriesMetadata()
    const indexOfLastPage = currentActiveQueryData.pageParams.length - 1
    const queriesArray = 'groups' in currentActiveQueryData ? groupQueries : pageQueries
    const endCursor = queriesArray[indexOfLastPage]?.data?.pageInfo.endCursor
    const newPageParam: PageParam = endCursor == null ? pageParamForInitialPage : {after: endCursor}
    setActiveQueriesMetadata({
      ...currentActiveQueryData,
      pageParams: [...currentActiveQueryData.pageParams, newPageParam],
    })
  }, [getActiveQueriesMetadata, groupQueries, pageQueries, setActiveQueriesMetadata])

  const queryClient = useQueryClient()
  const invalidateAllQueries = useCallback(async () => {
    // For current testing purposes, we're only tackling a single page for live updates
    if (queryKeys.length === 1) {
      for (const key of queryKeys) {
        await queryClient.invalidateQueries(key)
      }
    }
  }, [queryKeys, queryClient])

  const currentActiveQueryData = getActiveQueriesMetadata()
  const indexOfLastPage = currentActiveQueryData.pageParams.length - 1
  const queriesArray = 'groups' in currentActiveQueryData ? groupQueries : pageQueries
  const lastPageData = queriesArray[indexOfLastPage]?.data
  const hasNextPage = lastPageData == null || lastPageData.pageInfo.hasNextPage

  const isFetchingNextPage = queriesArray[indexOfLastPage]?.isFetching ?? false
  const isRefetching = queriesArray.some(query => query.isRefetching)

  return {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    invalidateAllQueries,
  }
}

function useMemexItemsGroupPagination(
  pageQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  queryKeys: Array<QueryKey>,
  variables: PaginatedMemexItemsQueryVariables,
) {
  const {getActiveQueriesMetadata, setActiveQueriesMetadata} = useMemexItemsActiveQueriesMetadata(variables)
  const fetchNextPageForGroup = useCallback(
    (groupId: string) => {
      const currentActiveQueryData = getActiveQueriesMetadata()
      if (!('groups' in currentActiveQueryData)) {
        return
      }
      const groupIndex = currentActiveQueryData.groups.findIndex(g => g.groupId === groupId)
      const group = currentActiveQueryData.groups[groupIndex]
      if (!group) {
        return
      }

      const lastPageParam = group.pageParams[group.pageParams.length - 1]
      const queryKeyForPageInGroup = buildMemexItemsPageQueryKey(variables, groupId, lastPageParam)
      const pageQuery = getPageQueryForQueryKey(queryKeyForPageInGroup, pageQueries, queryKeys)
      const endCursor = pageQuery?.data?.pageInfo.endCursor
      const newPageParam: PageParam = endCursor == null ? pageParamForInitialPage : {after: endCursor}

      const newGroup = {...group, pageParams: [...group.pageParams, newPageParam]}

      const newGroups = [
        ...currentActiveQueryData.groups.slice(0, groupIndex),
        newGroup,
        ...currentActiveQueryData.groups.slice(groupIndex + 1),
      ]
      setActiveQueriesMetadata({...currentActiveQueryData, groups: newGroups})
    },
    [getActiveQueriesMetadata, pageQueries, queryKeys, setActiveQueriesMetadata, variables],
  )

  const hasNextPageForGroup = useCallback(
    (groupId: string) => {
      const currentActiveQueryData = getActiveQueriesMetadata()
      if (!('groups' in currentActiveQueryData)) {
        return false
      }

      // If we can't find the group, then we will return false.
      const group = currentActiveQueryData.groups.find(g => g.groupId === groupId)
      if (!group) {
        return false
      }

      // Otherwise, build the query key for the last page in the group.
      const lastPageParam = group.pageParams[group.pageParams.length - 1]
      const queryKeyForPageInGroup = buildMemexItemsPageQueryKey(variables, groupId, lastPageParam)
      const pageQuery = getPageQueryForQueryKey(queryKeyForPageInGroup, pageQueries, queryKeys)

      // If we're unable to find a query matching the key, then we will return false.
      if (!pageQuery) {
        return false
      }

      // The last page in the group will have a null data value if it hasn't been fetched yet,
      // in which case we still have a `hasNextPage` value of true - otherwise,
      // return the `hasNextPage` value from our query data.
      const lastGroupPageData = pageQuery.data
      return lastGroupPageData == null || lastGroupPageData.pageInfo.hasNextPage
    },
    [getActiveQueriesMetadata, pageQueries, queryKeys, variables],
  )

  const isFetchingNextPageForGroup = useCallback(
    (groupId: string) => {
      const currentActiveQueryData = getActiveQueriesMetadata()
      if (!('groups' in currentActiveQueryData)) {
        return false
      }

      // If we can't find the group, then we will return false.
      const group = currentActiveQueryData.groups.find(g => g.groupId === groupId)
      if (!group) {
        return false
      }

      // Otherwise, build the query key for the last page in the group.
      const lastPageParam = group.pageParams[group.pageParams.length - 1]
      const queryKeyForPageInGroup = buildMemexItemsPageQueryKey(variables, groupId, lastPageParam)
      const pageQuery = getPageQueryForQueryKey(queryKeyForPageInGroup, pageQueries, queryKeys)

      return pageQuery?.isFetching
    },
    [getActiveQueriesMetadata, pageQueries, queryKeys, variables],
  )

  return {
    fetchNextPageForGroup,
    hasNextPageForGroup,
    isFetchingNextPageForGroup,
  }
}

function getPageQueryForQueryKey(
  queryKey: QueryKey,
  pageQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  queryKeys: Array<QueryKey>,
) {
  // We want to correlate that query key to a query, but since query keys are arrays,
  // we need to hash the key first before performing the comparison
  const hashForQueryKey = hashQueryKey(queryKey)
  const queryKeyIndex = queryKeys.findIndex(key => hashQueryKey(key) === hashForQueryKey)
  return pageQueries[queryKeyIndex]
}

function separateQueriesForItemsAndGroups(
  pageQueries: Array<UseQueryResult<MemexItemsPageQueryData | MemexItemGroupsPageQueryData>>,
  pageQueryKeys: Array<PaginatedMemexItemsQueryKey>,
) {
  const queriesForItems: Array<UseQueryResult<MemexItemsPageQueryData>> = []
  const queryKeysForItems: Array<PaginatedMemexItemsQueryKey> = []
  const queriesForGroups: Array<UseQueryResult<MemexItemGroupsPageQueryData>> = []
  const queryKeysForGroups: Array<PaginatedMemexItemsQueryKey> = []

  for (const [index, query] of pageQueries.entries()) {
    const queryKey = pageQueryKeys[index] as PaginatedMemexItemsQueryKey
    // We want to separate out queries for groups from queries for items based on the query key.
    // We unfortunately cannot rely on the shape of the data, as it might still be undefined,
    // while the data is loading.
    if (isQueryKeyForGroups(queryKey)) {
      queriesForGroups.push(query as UseQueryResult<MemexItemGroupsPageQueryData>)
      queryKeysForGroups.push(queryKey)
    } else {
      queriesForItems.push(query as UseQueryResult<MemexItemsPageQueryData>)
      queryKeysForItems.push(queryKey)
    }
  }
  return {
    queriesForItems,
    queryKeysForItems,
    queriesForGroups,
    queryKeysForGroups,
  }
}

type ItemQueriesByGroup = Record<
  NonNullable<GroupId>,
  {
    groupId: NonNullable<GroupId>
    groupName: string
    queries: Array<UseQueryResult<MemexItemsPageQueryData>>
  }
>

/**
 * Given all our queries for items and groups, we want to map all of the item queries to their corresponding group.
 * If we don't have any group queries, we will return an empty object.
 * Otherwise, we will group all of the item queries by their group id (which is extracted from its query key).
 * Then we will return an object that maps each group id to an array of item queries, along with the group name.
 */
function groupItemQueriesByGroupId(
  itemQueries: Array<UseQueryResult<MemexItemsPageQueryData>>,
  itemQueryKeys: Array<PaginatedMemexItemsQueryKey>,
  groupQueries: Array<UseQueryResult<MemexItemGroupsPageQueryData>>,
): ItemQueriesByGroup {
  const itemQueriesByGroup: Record<string, Array<UseQueryResult<MemexItemsPageQueryData>>> = {}

  // If we aren't grouped, we can return early
  if (groupQueries.length === 0) {
    return {}
  }

  // Let's iterate through all of our item query keys and group their corresponding queries by group id.
  for (const [index, queryKey] of itemQueryKeys.entries()) {
    const groupId = getGroupIdFromQueryKey(queryKey)
    const relatedQuery = itemQueries[index]
    if (relatedQuery && groupId) {
      const queriesForGroup = itemQueriesByGroup[groupId] || []
      queriesForGroup.push(relatedQuery)
      itemQueriesByGroup[groupId] = queriesForGroup
    }
  }

  // Each group query in groupQueries represents a page of groups, so let's flatten all of the groups into a single array.
  const groups = groupQueries.flatMap(q => q.data?.groups || [])

  // We want to iterate over each of these groups and extract the group id and group name from each group,
  // and then look up the item queries for that group that we built in the previous step.
  return groups.reduce((groupsById, group) => {
    return {
      ...groupsById,
      [group.groupId]: {
        groupId: group.groupId,
        groupName: group.groupName,
        queries: itemQueriesByGroup[group.groupId] || [],
      },
    }
  }, {} as ItemQueriesByGroup)
}
